import URI from 'jsuri';
import Q from 'q';

import API from 'js/lib/api';

import type {
  RawSubmissionDraftResponse,
  SubmissionDraftResponse,
  SubmissionDraftUpdate,
} from 'bundles/peer/types/NaptimeSubmission';

const onDemandPeerSubmissionDraftsApi = API('/api/onDemandPeerSubmissionDrafts.v1', { type: 'rest' });

const submissionDraftFields = [
  'submission',
  'context',
  'creatorId',
  'attachedAssignmentId',
  'createdAt',
  'upgradeSubmissionToLatestAssignment',
  'blocksSubmit',
  'validationErrors',
  'onDemandPeerReviewSchemas.v1(reviewSchema)',
  'onDemandPeerSubmissionSchemas.v1(submissionSchema)',
];

const submissionDraftIncludes = ['reviewSchemas', 'submissionSchemas'];

/*
 * The backend should guarantee that if no errors are thrown, there exists a single draft element. However, we want
 * to return a single element to the caller instead of the normal naptime elements response for ease of use, and
 * if we don't throw an error if this "impossible" case ocurrs it could be difficult to debug.
 */
function responseHandler(response: $TSFixMe /* TODO: type onDemandPeerSubmissionDraftsApi */) {
  if (response.elements && response.elements.length) {
    const submissionSchema =
      response.linked['onDemandPeerSubmissionSchemas.v1'] &&
      response.linked['onDemandPeerSubmissionSchemas.v1'].length &&
      response.linked['onDemandPeerSubmissionSchemas.v1'][0];
    const reviewSchema =
      response.linked['onDemandPeerReviewSchemas.v1'] &&
      response.linked['onDemandPeerReviewSchemas.v1'].length &&
      response.linked['onDemandPeerReviewSchemas.v1'][0];

    if (submissionSchema == null) {
      throw new Error('No linked submission schema in onDemandPeerSubmissionDraftsApi');
    }
    return {
      ...response.elements[0],
      reviewSchema,
      submissionSchema,
    };
  }

  throw new Error('No elements received in onDemandPeerSubmissionDraftsApi');
}

export const getSubmissionDraft = ({
  userId,
  courseId,
  itemId,
}: {
  userId: number;
  courseId: string;
  itemId: string;
}): Q.Promise<SubmissionDraftResponse> => {
  const id = `${userId}~${courseId}~${itemId}`;
  const uri = new URI(id)
    .addQueryParam('fields', submissionDraftFields.join(','))
    .addQueryParam('includes', submissionDraftIncludes.join(','));

  return Q(onDemandPeerSubmissionDraftsApi.get(uri.toString())).then(responseHandler);
};

export const updateSubmissionDraft = ({
  userId,
  courseId,
  itemId,
  submissionDraftUpdate,
}: {
  userId: number;
  courseId: string;
  itemId: string;
  submissionDraftUpdate: SubmissionDraftUpdate;
}): Q.Promise<SubmissionDraftResponse> => {
  const id = `${userId}~${courseId}~${itemId}`;
  const uri = new URI(id)
    .addQueryParam('fields', submissionDraftFields.join(','))
    .addQueryParam('includes', submissionDraftIncludes.join(','));

  return Q(
    onDemandPeerSubmissionDraftsApi.put(uri.toString(), {
      data: submissionDraftUpdate,
    })
  ).then(responseHandler);
};

export const forceUpgradedSubmissionDraft = ({
  userId,
  courseId,
  itemId,
  forceUpgradeToAssignmentId,
}: {
  userId: number;
  courseId: string;
  itemId: string;
  forceUpgradeToAssignmentId: string;
}): Q.Promise<RawSubmissionDraftResponse> => {
  const id = `${userId}~${courseId}~${itemId}`;
  const uri = new URI()
    .addQueryParam('action', 'forceUpgradedDraft')
    .addQueryParam('id', id)
    .addQueryParam('forceUpgradeToAssignmentId', forceUpgradeToAssignmentId)
    .addQueryParam('fields', submissionDraftFields.join(','))
    .addQueryParam('includes', submissionDraftIncludes.join(','));

  return Q(onDemandPeerSubmissionDraftsApi.post(uri.toString())).then((response) => response[0]);
};
